<script setup lang="ts">
import { Search as SearchIcon } from '@lucide/vue'
import { onClickOutside } from '@vueuse/core'
import { computed, ref, type ComponentPublicInstance } from 'vue'

import SmartSearchResults from '@/components/map/SmartSearchResults.vue'
import { useSmartSearch } from '@/composables/useSmartSearch'
import { isSmallScreen } from '@/services/uiService'

const props = withDefaults(
  defineProps<{
    expandedWidth?: string | null
  }>(),
  {
    expandedWidth: null,
  },
)

const search = useSmartSearch()
const { isOpen, query, inputRef, handleInputKeydown, toggleSearch, shouldShowResultsDropdown } =
  search
const containerRef = ref<HTMLElement | null>(null)
const setInputRef = (element: Element | ComponentPublicInstance | null) => {
  inputRef.value = element instanceof HTMLInputElement ? element : null
}

const expandedWidth = computed(
  () =>
    props.expandedWidth ??
    (isSmallScreen.value ? 'calc(100vw - 32px)' : 'min(28rem, calc(100vw - 32px))'),
)
const rootStyle = computed(() => ({
  width: isOpen.value ? expandedWidth.value : '40px',
}))

onClickOutside(containerRef, () => {
  if (isOpen.value) {
    search.closeSearch()
  }
})
</script>

<template>
  <div
    ref="containerRef"
    data-testid="map-smart-search"
    class="pointer-events-auto relative transition-[width] duration-300 ease-out"
    :style="rootStyle"
  >
    <div
      class="relative flex h-10 items-center overflow-hidden border border-black/[0.08] bg-white/92 text-gray-600 backdrop-blur-sm"
      :class="
        isOpen
          ? shouldShowResultsDropdown
            ? 'rounded-t-[20px] rounded-b-none'
            : 'rounded-[20px]'
          : 'rounded-full'
      "
    >
      <button
        class="inline-flex h-10 w-10 shrink-0 items-center justify-center transition-colors hover:bg-black/[0.03] hover:text-gray-900 hover:cursor-pointer"
        :aria-expanded="isOpen"
        :aria-label="isOpen ? 'Close smart search' : 'Open smart search'"
        :title="isOpen ? 'Close smart search' : 'Open smart search'"
        @click="toggleSearch"
      >
        <SearchIcon class="h-[18px] w-[18px]" />
      </button>

      <div
        class="min-w-0 transition-[opacity] duration-200"
        :class="isOpen ? 'flex-1 border-l border-black/10 opacity-100' : 'w-0 opacity-0'"
      >
        <input
          v-if="isOpen"
          :ref="setInputRef"
          v-model="query"
          data-testid="map-smart-search-input"
          type="text"
          autocomplete="off"
          spellcheck="false"
          class="h-10 w-full min-w-0 bg-transparent px-3 text-sm text-slate-900 outline-none placeholder:text-slate-400"
          placeholder="Search places, collections, activities, and routes..."
          @keydown="handleInputKeydown"
        />
      </div>
    </div>

    <div
      v-if="isOpen && shouldShowResultsDropdown"
      data-testid="map-smart-search-results"
      class="absolute left-0 right-0 top-full -mt-px overflow-hidden rounded-b-[20px] rounded-t-none border border-t-0 border-black/[0.08] bg-white/92 text-slate-900 backdrop-blur-sm"
    >
      <div class="max-h-[min(60vh,28rem)] overflow-x-hidden overflow-y-auto">
        <SmartSearchResults :search="search" />
      </div>
    </div>
  </div>
</template>
